import React, { useState } from "react";
import { Button, Input } from "reactstrap";
import LaddaButton, { EXPAND_RIGHT } from "react-ladda";
import Files from "./NewPost/Files/Files";
import style from "./Items.module.scss";
import { showToast } from "../../../../utils/Helper";

// Inline comment/attachment form. Mirrors the data-flow of NewPost (calls
// fetchs.addToHistory then optionally fetchs.uploadFile, refreshes via
// fetchs.refreshAlert / fetchs.alert). No "send to role" controls — that
// lives in ChangesActions next to the rest of the action buttons.
const AVATAR_PALETTE = [
  "#455a64",
  "#1c84c6",
  "#1ab394",
  "#f8ac59",
  "#e74c3c",
  "#9b59b6",
  "#607d8b",
];

const hashString = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

const getAvatarColor = (name) =>
  name ? AVATAR_PALETTE[hashString(String(name)) % AVATAR_PALETTE.length] : AVATAR_PALETTE[0];

const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const cardStyle = {
  marginTop: 12,
  background: "#ffffff",
  border: "1px solid #e3e8ec",
  borderRadius: "8px",
  padding: "14px 16px",
  boxShadow: "0 1px 0 rgba(38, 50, 56, 0.02)",
};

const headerStyle = {
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: "#90a4ae",
  marginBottom: "10px",
};

const userRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "8px",
};

const avatarStyle = {
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  color: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.4px",
};

const usernameStyle = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#263238",
};

const actionsRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "10px",
};

const CommentForm = ({ alertId, fetchs, onSubmitted, onCancel, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});

  const handleFilesUpload = (event) => {
    const addedFiles = Object.values(event.target.files);
    const reservedCharsRegex = /[!@#$%^&*()+={}\[\]|\\:;"'<>,?/]/;

    const validFiles = addedFiles.filter((f) => {
      const isTooLong = f.name.length >= 200;
      const hasReservedChars = reservedCharsRegex.test(f.name);
      return !isTooLong && !hasReservedChars;
    });

    if (validFiles.length !== addedFiles.length) {
      showToast(
        "Some files were not added because their names are too long or contain invalid characters (e.g., %, /, +, #, etc.).",
        true
      );
    }

    setFiles([
      ...validFiles.map((f) => ({
        id: Math.random().toString(36).substring(2, 15),
        name: f.name,
        type: f.type,
        data: f,
      })),
    ]);
  };

  const deleteFile = (id) => {
    setFiles(files.filter((f) => f.id != id));
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const result = String(r.result || "");
        const parts = result.split(",");
        resolve(parts.length > 1 ? parts[1] : "");
      };
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const buildAlertFilesPayload = async (filesToBuild) => {
    const items = await Promise.all(
      filesToBuild.map(async (f) => {
        const content = await readFileAsBase64(f.data);
        const name = f.name || "";
        const parts = name.split(".");
        let ext = "";
        if (parts.length > 1) {
          ext = parts.pop().trim();
          if (ext.charAt(0) === ".") ext = ext.substring(1);
        }
        const fileNameOnly = parts.join(".");
        return { fileName: fileNameOnly, extension: ext, content };
      })
    );
    return items;
  };

  const reset = () => {
    setFiles([]);
    setDescription("");
    setErrors({});
  };

  const refresh = () => {
    if (fetchs && fetchs.refreshAlert) fetchs.refreshAlert();
    else if (fetchs && fetchs.alert) fetchs.alert(alertId);
  };

  const submit = () => {
    if (!description) {
      setErrors((e) => ({ ...e, description: "Required Field" }));
      return;
    }
    if (!fetchs || !fetchs.addToHistory) return;

    setLoading(true);

    const getLocalISOTime = () => {
      const date = new Date();
      const tzOffset = -date.getTimezoneOffset() * 60 * 1000;
      const localDate = new Date(date.getTime() + tzOffset);
      return localDate.toISOString().slice(0, -1);
    };

    fetchs.addToHistory(
      {
        eventId: alertId,
        message: description,
        dateTime: getLocalISOTime(),
      },
      async (resp) => {
        try {
          const historyEventId = resp && resp.historyEventId;
          if (!historyEventId) throw new Error("Missing historyEventId in addToHistory response.");

          if (!files || files.length === 0) {
            refresh();
            setLoading(false);
            reset();
            if (onSubmitted) onSubmitted();
            return;
          }

          const filesPayload = await buildAlertFilesPayload(files);
          fetchs.uploadFile(
            {
              eventId: alertId,
              historyEventId,
              files: filesPayload,
            },
            () => {
              refresh();
              setLoading(false);
              reset();
              if (onSubmitted) onSubmitted();
            }
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("addToHistory/uploadFile error:", err);
          setLoading(false);
          setErrors((e) => ({ ...e, files: "Error uploading files. Please try again." }));
        }
      }
    );
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>New comment</div>

      {currentUser && (
        <div style={userRowStyle}>
          <div style={{ ...avatarStyle, background: getAvatarColor(currentUser) }}>
            {getInitials(currentUser)}
          </div>
          <span style={usernameStyle}>{currentUser}</span>
        </div>
      )}

      <Input
        type="textarea"
        rows={3}
        placeholder="Write a comment..."
        value={description}
        disabled={loading}
        invalid={!!errors.description || description.length > 5000}
        style={description.length > 5000 ? { borderColor: "#e74c3c" } : {}}
        onChange={(e) => {
          if (e.target.value.length <= 5000) {
            setDescription(e.target.value);
            if (e.target.value !== "") {
              setErrors({ ...errors, description: undefined });
            }
          } else {
            setErrors({ ...errors, description: "Max Characters Limit Reached" });
          }
        }}
      />
      {errors.description && (
        <div style={{ color: "#e74c3c", fontSize: 12, marginTop: 4 }}>{errors.description}</div>
      )}

      <Files files={files} deleteFile={deleteFile} />

      <div style={actionsRowStyle}>
        <Button
          color="light"
          size="sm"
          style={{ position: "relative", overflow: "hidden" }}
        >
          <i className="fa fa-paperclip mr-2" />
          <input
            type="file"
            className={style.inputFile}
            multiple
            onChange={(e) => handleFilesUpload(e)}
          />
          Attach
        </Button>
        <div style={{ flex: 1 }} />
        <Button
          color="light"
          size="sm"
          onClick={() => {
            reset();
            if (onCancel) onCancel();
          }}
        >
          Discard
        </Button>
        <LaddaButton
          className="btn btn-ladda btn-primary btn-sm"
          loading={loading}
          onClick={submit}
          data-style={EXPAND_RIGHT}
        >
          Add
        </LaddaButton>
      </div>
      {(errors.files || description.length > 5000) && (
        <span style={{ display: "block", color: "#e74c3c", fontSize: 12, marginTop: 6 }}>
          {errors.files ? errors.files : ""}
          {description.length > 5000 ? "Max Characters Limit Reached" : ""}
        </span>
      )}
    </div>
  );
};

export default CommentForm;
